import { useState, type ReactNode } from "react";
import { AlertTriangle, Armchair, Cross, FileText, House, PawPrint, Pencil, Pill, Plus, Settings, Users } from "lucide-react";
import { Modal } from "@fawkes/ui";

import { BasisTile, FeedingTile, MediaTile, MedicijnenTile, NotesTile, VaccinationsTile, WeightsTile } from "../animalTiles";
import { LogboekTile } from "../LogboekTile";
import type { PensionDier } from "../pensionKlantTypes";
import { entingStaat, geslachtTeken, leeftijd, type Context, type Vertaal } from "./hulp";
import { TegelInstellen, TegelsInstellen } from "./Instellen";
import { KopieerKnop, TEGEL_COMPONENT, TegelActiesContext, type DossierSleutel, type TegelActies } from "./Tegels";
import type { TegelSleutel } from "./tegelDefinities";
import { useDieroverzichtVoorkeur } from "./useDieroverzichtVoorkeur";
import { useCustom } from "@refinedev/core";
import { EigenTegel } from "./EigenTegel";
import { TegelBouwer } from "./TegelBouwer";
import { dichtheidBijAantal } from "./tegelOntwerpen";
import {
  MAX_SAMENGESTELD, nieuweSamengesteldeTegel, type SamengesteldeTegel,
} from "./samengesteldeTegels";

// Geen subtabblad Verblijven: de boekingen staan op het tabblad Pension van de klant.
type Sub = "overzicht" | "verzorging" | "medisch" | "notities";

const SUBS: { sleutel: Sub; icoon: ReactNode }[] = [
  { sleutel: "overzicht", icoon: <House size={18} strokeWidth={1.8} /> },
  { sleutel: "verzorging", icoon: <Armchair size={18} strokeWidth={1.8} /> },
  { sleutel: "medisch", icoon: <Cross size={18} strokeWidth={1.8} /> },
  { sleutel: "notities", icoon: <FileText size={18} strokeWidth={1.8} /> },
];

/**
 * De echte tegels uit het dierdossier, groot in een venster: vanaf Dier bewerken en vanaf elke tegel van
 * het overzicht. Vat een overzichtstegel twee dossiertegels samen, dan staan ze allebei in het venster.
 */
const DOSSIER: Record<DossierSleutel, { titel: string; toon: (id: string, t: Vertaal) => ReactNode }> = {
  basis: { titel: "animalDetailView.tile.basis", toon: (id) => <BasisTile animalId={id} /> },
  media: { titel: "animalDetailView.tile.media", toon: (id) => <MediaTile animalId={id} /> },
  verzorging: { titel: "animalDetailView.tile.voeding", toon: (id) => <FeedingTile animalId={id} /> },
  gewicht: { titel: "animalDetailView.tile.gewichten", toon: (id) => <WeightsTile animalId={id} /> },
  medisch: {
    titel: "pensionKlant.tegel.medisch",
    toon: (id, t) => (
      <div className="pk-dossier">
        <Dossier titel={t("animalDetailView.tile.bescherming")}>
          <VaccinationsTile animalId={id} />
        </Dossier>
        <Dossier titel={t("animalDetailView.tile.medicijnen")}>
          <MedicijnenTile animalId={id} />
        </Dossier>
      </div>
    ),
  },
  activiteiten: {
    titel: "pensionKlant.tegel.activiteiten",
    toon: (id, t) => (
      <div className="pk-dossier">
        <Dossier titel={t("animalDetailView.tile.notities")}>
          <NotesTile animalId={id} />
        </Dossier>
        <Dossier titel={t("animalDetailView.tile.logboek")}>
          <LogboekTile animalId={id} />
        </Dossier>
      </div>
    ),
  },
};

/**
 * Het gekozen dier, rechts op het tabblad Dieren.
 *
 * <p>Bovenaan wie het dier is. Daaronder subtabbladen: "Overzicht" toont de tegels die de gebruiker
 * zelf inricht — per tegel via het tandwiel, en welke tegels via "Tegels instellen". De andere
 * subtabbladen tonen de echte tegels uit het dierdossier; bewerken gebeurt daar, niet hier nog een keer.
 */
export function DierDetail({
  dier,
  foto,
  ctx,
  onGewijzigd,
  onNaarPension,
}: {
  dier: PensionDier;
  foto?: string;
  ctx: Context;
  /** Naar het tabblad Pension van de klant, waar alle boekingen staan. */
  onNaarPension: () => void;
  /** Na bewerken in een dossiertegel: de gegevens van de klant opnieuw ophalen. */
  onGewijzigd: () => void;
}) {
  const { t, vandaag } = ctx;
  const [sub, setSub] = useState<Sub>("overzicht");
  const [dossier, setDossier] = useState<DossierSleutel | null>(null);
  const [tegelInstellen, setTegelInstellen] = useState<TegelSleutel | null>(null);
  const [allesInstellen, setAllesInstellen] = useState(false);
  /** De eigen tegel die in de bouwer openstaat; "nieuw" maakt er een. */
  const [bouwer, setBouwer] = useState<SamengesteldeTegel | "nieuw" | null>(null);
  const { voorkeur, opslaan } = useDieroverzichtVoorkeur();
  const eigen = voorkeur.samengesteld;
  // Rekent deze vestiging per dier? Bepaalt of de dagprijs als onderdeel gekozen mag worden. Zonder
  // antwoord gaan we ervan uit van niet: een onderdeel dat nooit vult aanbieden is erger dan er een
  // verzwijgen tot de vestiging bekend is.
  const tariefPerDier =
    useCustom<{ prijsberekening?: string }>({ url: "pension/prijsberekening", method: "get" })
      .data?.data?.prijsberekening === "PENSIONPRIJZEN";
  // Het systeem kiest de dichtheid uit het aantal zichtbare tegels; de gebruiker kiest alleen wélke
  // tegels hij ziet en in welke volgorde.
  const dichtheid = dichtheidBijAantal(voorkeur.tegels.filter((x) => x.zichtbaar).length + eigen.length);

  const id = String(dier.id);
  const teken = geslachtTeken(dier.geslacht);
  const meta = [
    dier.geslacht,
    dier.gecastreerd ? t("pensionKlant.dier.gecastreerdJa") : t("pensionKlant.dier.gecastreerdNee"),
    leeftijd(dier.geboortedatum, ctx),
  ].filter(Boolean);
  const enting = entingStaat(dier, vandaag);
  const acties: TegelActies = {
    openDossierTegel: (tegel) => setDossier(tegel),
    naarSubtab: (s) => setSub(s),
    naarPension: onNaarPension,
    gewijzigd: onGewijzigd,
  };

  return (
    <section className="pk-paneel pk-dier" aria-label={dier.naam ?? undefined}>
      <header className="pk-dier__kop">
        {foto ? (
          <img className="pk-dier__foto" src={foto} alt="" />
        ) : (
          <span className="pk-dier__foto" aria-hidden="true">
            <PawPrint size={40} />
          </span>
        )}
        <div className="pk-dier__tekst">
          <h2>{dier.naam ?? "—"}</h2>
          <p className="pk-dier__soort">
            {[dier.diersoort, dier.ras].filter(Boolean).map((s, i) => (
              <span key={s}>
                {i > 0 && <span className="pk-punt" aria-hidden="true">·</span>}
                {s}
              </span>
            ))}
          </p>
          <p className="pk-dier__meta">
            {teken && (
              <span className="pk-dier__geslacht" aria-hidden="true">
                {teken}
              </span>
            )}
            {meta.map((s, i) => (
              <span key={s}>
                {i > 0 && <span className="pk-punt" aria-hidden="true">·</span>}
                {s}
              </span>
            ))}
          </p>
          <div className="pk-dier__onder">
            {dier.chipnummer && (
              <div className="pk-dier__chip">
                <span className="pk-dier__chiplabel">{t("pensionKlant.regel.chipnummer")}</span>
                <span className="pk-met-knop">
                  <strong>{dier.chipnummer}</strong>
                  <KopieerKnop tekst={dier.chipnummer} ctx={ctx} />
                </span>
              </div>
            )}
            <ul className="pk-dier__chips">
              <li className="pk-chip" data-toon="grijs">
                <Users size={14} aria-hidden="true" />
                {dier.bezoekNummer > 0
                  ? t("pensionKlant.dieren.bezoek", { args: [dier.bezoekNummer] })
                  : t("pensionKlant.dieren.eersteBezoek")}
              </li>
              {(enting === "verlopen" || enting === "binnenkort") && (
                <li className="pk-chip" data-toon="amber">
                  <AlertTriangle size={14} aria-hidden="true" />
                  {t(enting === "verlopen" ? "pensionKlant.signaal.entingVerlopen" : "pensionKlant.signaal.entingBinnenkort")}
                </li>
              )}
              {(dier.stand?.medicatieActief ?? 0) > 0 && (
                <li className="pk-chip" data-toon="accent">
                  <Pill size={14} aria-hidden="true" />
                  {t("pensionKlant.signaal.medicatieActief")}
                </li>
              )}
            </ul>
          </div>
        </div>
        <button type="button" className="pk-knop pk-dier__bewerken" onClick={() => setDossier("basis")}>
          <Pencil size={16} aria-hidden="true" />
          {t("pensionKlant.dier.bewerken")}
        </button>
      </header>

      <div className="pk-subtabs">
        <ul role="tablist" aria-label={t("pensionKlant.dier.subtabsAria")}>
          {SUBS.map((s) => (
            <li key={s.sleutel} role="presentation">
              <button
                type="button"
                role="tab"
                aria-selected={sub === s.sleutel}
                className="pk-subtab"
                onClick={() => {
                  // Terug naar het overzicht na een andere subtab: daar kan net iets zijn gewijzigd (een
                  // voedingsschema, een enting, medicatie). De stand van het dier komt mee met de klant,
                  // dus die opnieuw ophalen; anders zegt de tegel nog "Geen schema".
                  if (s.sleutel === "overzicht" && sub !== "overzicht") onGewijzigd();
                  setSub(s.sleutel);
                }}
              >
                <span aria-hidden="true">{s.icoon}</span>
                {t(`pensionKlant.sub.${s.sleutel}`)}
              </button>
            </li>
          ))}
        </ul>
        {sub === "overzicht" && (
          <>
            {eigen.length < MAX_SAMENGESTELD && (
              <button type="button" className="pk-knop pk-knop--zacht" onClick={() => setBouwer("nieuw")}>
                <Plus size={16} aria-hidden="true" />
                {t("dierTegel.bouwer.maken")}
              </button>
            )}
            <button type="button" className="pk-knop pk-knop--zacht" onClick={() => setAllesInstellen(true)}>
              <Settings size={16} aria-hidden="true" />
              {t("pensionKlant.instellen.tegelsKnop")}
            </button>
          </>
        )}
      </div>

      <TegelActiesContext.Provider value={acties}>
      <div role="tabpanel" className="pk-dier__inhoud">
        {sub === "overzicht" && (
          <div className="pk-raster">
            {voorkeur.tegels
              .filter((x) => x.zichtbaar)
              .map((inst) => {
                const Component = TEGEL_COMPONENT[inst.sleutel];
                return (
                  <Component
                    key={inst.sleutel}
                    dier={dier}
                    inst={inst}
                    ctx={ctx}
                    acties={acties}
                    onInstellen={() => setTegelInstellen(inst.sleutel)}
                  />
                );
              })}
            {eigen.map((eigenTegel) => (
              <EigenTegel
                key={eigenTegel.id}
                tegel={eigenTegel}
                dier={dier}
                foto={foto}
                dichtheid={dichtheid}
                vandaag={vandaag}
                onInstellen={() => setBouwer(eigenTegel)}
              />
            ))}
            {voorkeur.tegels.every((x) => !x.zichtbaar) && eigen.length === 0 && (
              <p className="pk-raster__leeg">{t("pensionKlant.instellen.geenTegels")}</p>
            )}
          </div>
        )}
        {sub === "verzorging" && (
          <div className="pk-dossier">
            <Dossier titel={t("animalDetailView.tile.voeding")}>
              <FeedingTile animalId={id} />
            </Dossier>
            <Dossier titel={t("animalDetailView.tile.gewichten")}>
              <WeightsTile animalId={id} />
            </Dossier>
          </div>
        )}
        {sub === "medisch" && (
          <div className="pk-dossier">
            <Dossier titel={t("animalDetailView.tile.bescherming")}>
              <VaccinationsTile animalId={id} />
            </Dossier>
            <Dossier titel={t("animalDetailView.tile.medicijnen")}>
              <MedicijnenTile animalId={id} />
            </Dossier>
          </div>
        )}
        {sub === "notities" && (
          <div className="pk-dossier">
            <Dossier titel={t("animalDetailView.tile.notities")}>
              <NotesTile animalId={id} />
            </Dossier>
            <Dossier titel={t("animalDetailView.tile.logboek")}>
              <LogboekTile animalId={id} />
            </Dossier>
          </div>
        )}
      </div>
      </TegelActiesContext.Provider>

      {bouwer && (
        <TegelBouwer
          tegel={bouwer === "nieuw" ? nieuweSamengesteldeTegel(eigen.length) : bouwer}
          tariefPerDier={tariefPerDier}
          onSluit={() => setBouwer(null)}
          onBewaar={(nieuw) => {
            const zonder = eigen.filter((x) => x.id !== nieuw.id);
            void opslaan({ ...voorkeur, samengesteld: [...zonder, nieuw] });
            setBouwer(null);
          }}
          onVerwijder={bouwer === "nieuw" ? undefined : () => {
            void opslaan({ ...voorkeur, samengesteld: eigen.filter((x) => x.id !== bouwer.id) });
            setBouwer(null);
          }}
        />
      )}

      <TegelInstellen
        sleutel={tegelInstellen}
        voorkeur={voorkeur}
        dier={dier}
        ctx={ctx}
        onClose={() => setTegelInstellen(null)}
        onOpslaan={opslaan}
      />
      <TegelsInstellen
        open={allesInstellen}
        voorkeur={voorkeur}
        ctx={ctx}
        onClose={() => setAllesInstellen(false)}
        onOpslaan={opslaan}
      />
      <Modal
        open={dossier !== null}
        title={dossier ? t(DOSSIER[dossier].titel) : ""}
        onClose={() => {
          setDossier(null);
          onGewijzigd();
        }}
        size="wide"
      >
        {/* Het venster staat buiten het klantscherm; .pk geeft het de kleuren en maten van de tegels. */}
        {dossier && <div className="pk pension-groot">{DOSSIER[dossier].toon(id, t)}</div>}
      </Modal>
    </section>
  );
}

function Dossier({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <section className="pk-tegel pk-tegel--dossier" aria-label={titel}>
      <header className="pk-tegel__kop">
        <h3>{titel}</h3>
      </header>
      <div className="pk-tegel__inhoud">{children}</div>
    </section>
  );
}
